/* eslint-disable sonarjs/cognitive-complexity */
import { Auth0Client } from "@auth0/auth0-spa-js";

import URLS from "@config/urls";
import { logger as baseLogger } from "@core/logger";
import { Network, ProductionDataResponse } from "@core/models";
import { urlWithParams } from "@core/utils/urlWithParams";

import { AxiosApiClient } from "../base/apiClient";
import { ApiResponse, RadiatorCurve } from "../shared/types";
import {
  BaseMeterDataParams,
  GetSingleSubstationMeterDataParams,
  GetSubstationsMeterDataParams,
  InfoDataResponse,
  MeterData,
  MeterDataResponse,
  PagedResponse,
  ValidMeter,
  ValidMeterComponent,
  ValidMeterStage,
} from "./types";

const logger = baseLogger.getSubLogger({ name: "mdsl.api" });

/**
 * @deprecated Use PlatformDataApi instead
 */
export class MdslApi extends AxiosApiClient {
  constructor(
    getTokenOrAuth0Client: (() => Promise<string>) | Auth0Client,
    authClient: Auth0Client | undefined
  ) {
    super(URLS.mdslv4, getTokenOrAuth0Client, authClient);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private async fetchPagedData<T extends { data?: any[] }>(
    url: string,
    params: Record<string, string>,
    transformBlock: (block: any) => T
  ): Promise<{ collection: T[] } | null> {
    let currentPage = 1;
    let haveMore = true;
    const readings = new Map<string, T>();

    try {
      while (haveMore) {
        params.page = currentPage.toString();
        const fullUrl = urlWithParams(url, params);

        const response = await this.client.get<PagedResponse<any>>(fullUrl);

        if (response.status === 200 && response.data) {
          const resJson = response.data;
          if (resJson.data && resJson.error === null) {
            for (const blk of resJson.data.collection) {
              const blk_id = `${blk.uid}-${blk.component}-${blk.stage}`;
              if (readings.has(blk_id)) {
                const prevReading = readings.get(blk_id)!;
                if (prevReading.data && Array.isArray(prevReading.data)) {
                  prevReading.data.push(...blk.data);
                }
              } else {
                readings.set(blk_id, transformBlock(blk));
              }
            }
            if (resJson.data.has_next_page) {
              currentPage += 1;
            } else {
              haveMore = false;
            }
          } else {
            return null;
          }
        } else {
          return null;
        }
      }
      return { collection: Array.from(readings.values()) };
    } catch (err) {
      logger.error("fetchPagedData", err);
      return null;
    }
  }

  async getNetwork(network_uid: string): Promise<Network | null> {
    logger.trace("getNetwork", { network_uid });
    return this.client
      .get<ApiResponse<Network>>(`/networks/${network_uid}`)
      .then((response) => response.data.data)
      .catch((err) => {
        logger.error("getNetwork", err);
        return err.error || "Error fetching network";
      });
  }

  async getSubstationsMeterData({
    network_uid,
    resource_uids = [],
    stage = "clean",
    meter = "primary",
    components = [],
    flags = [],
    range = {},
    ts_end,
    ts_start,
    page_size = 0,
  }: GetSubstationsMeterDataParams): Promise<MeterDataResponse | null> {
    logger.trace("getSubstationsMeterData", { network_uid, resource_uids });

    const params: Record<string, string> = {
      "component[in]": components.join(","),
      "stage[in]": stage,
      "meter[in]": meter,
      page_size: page_size.toString(),
      "datetime[ge]": ts_start.toISO() || "",
      "datetime[lt]": ts_end.toISO() || "",
    };

    // Add range parameters
    for (const [key, value] of Object.entries(range)) {
      params[key] = value;
    }

    // Add resource UIDs if provided
    if (resource_uids.length > 0) {
      params["uid[in]"] = resource_uids.join(",");
    }

    // Add flags if provided
    if (flags.length > 0) {
      params["flag[in]"] = flags.join(",");
    }

    return this.fetchPagedData(
      `/networks/${network_uid}/substations/meter-data`,
      params,
      (block) => block
    ) as Promise<MeterDataResponse | null>;
  }

  async getSubstationMeterData({
    network_uid,
    substation_uid,
    stage = "clean",
    meter = "primary",
    components = [],
    flags = [],
    range = {},
    ts_start,
    ts_end,
    page_size = 0,
  }: GetSingleSubstationMeterDataParams): Promise<MeterDataResponse | null> {
    const params: Record<string, string> = {
      "uid[in]": substation_uid,
      "component[in]": components.join(","),
      "stage[in]": stage,
      "meter[in]": meter,
      page_size: page_size.toString(),
      "datetime[ge]": ts_start.toISO() || "",
      "datetime[lt]": ts_end.toISO() || "",
    };

    // Add range parameters
    for (const [key, value] of Object.entries(range)) {
      params[key] = value;
    }

    // Add flags if provided
    if (flags.length > 0) {
      params["flag[in]"] = flags.join(",");
    }

    return this.fetchPagedData(
      `/networks/${network_uid}/substations/meter-data`,
      params,
      (block) => block
    ) as Promise<MeterDataResponse | null>;
  }

  async getMultipleSeriesData(
    networkUID: string,
    params: Record<string, string>
  ): Promise<ProductionDataResponse> {
    logger.trace("getMultipleSeriesData", { networkUID, params });

    try {
      const url = `/dhprod/networks/${networkUID}/seriesdata`;
      const queryParams = { ...params, stage: "OBSERVATIONS_RAW", resample: "1 hour" };

      const response = await this.client.get<ProductionDataResponse>(url, {
        params: queryParams,
      });

      if (response.status === 200 && response.data) {
        return response.data;
      }

      throw new Error(`Failed to fetch production data for network ${networkUID}`);
    } catch (err) {
      logger.error("getMultipleSeriesData", { err, networkUID });
      throw err;
    }
  }

  // Radiator curve methods using MDSL v5 endpoints
  async getRadiatorCurvesBySubstation(substationUid: string): Promise<RadiatorCurve[]> {
    logger.debug("getRadiatorCurvesBySubstation request", { substationUid });

    try {
      // Use v5 endpoint - construct from current baseUrl
      const baseUrl = this.baseUrl.replace(/\/v[0-9]+$/, ""); // Remove version suffix
      const response = await this.client.get(
        `${baseUrl}/v5/substations/${substationUid}/radiator-curves`
      );

      if (response.data?.data?.collection) {
        return response.data.data.collection;
      } else if (Array.isArray(response.data?.data)) {
        return response.data.data;
      } else if (response.data?.collection) {
        return response.data.collection;
      }

      return [];
    } catch (error) {
      logger.error("getRadiatorCurvesBySubstation error", { error, substationUid });
      throw new Error(error?.response?.data?.error?.message || "Failed to fetch radiator curves");
    }
  }

  async getRadiatorCurveByFlexstation(
    substationUid: string,
    flexstationUid: string
  ): Promise<RadiatorCurve | null> {
    logger.debug("getRadiatorCurveByFlexstation request", { substationUid, flexstationUid });

    try {
      // Use v5 endpoint - construct from current baseUrl
      const baseUrl = this.baseUrl.replace(/\/v[0-9]+$/, ""); // Remove version suffix
      const response = await this.client.get(
        `${baseUrl}/v5/substations/${substationUid}/flexstations/${flexstationUid}/radiator-curve`
      );

      return response.data?.data || null;
    } catch (error) {
      logger.error("getRadiatorCurveByFlexstation error", {
        error,
        substationUid,
        flexstationUid,
      });
      // Return null instead of throwing for optional data
      return null;
    }
  }

  // /v4/networks/{networkUID}/substations/info-data
  async getInfoData(
    networkUid: string,
    options: {
      resourceUids: string[];
      blockNames: string[];
      page?: number;
      pageSize?: number;
    }
  ): Promise<InfoDataResponse> {
    const params: Record<string, any> = {
      "uid[in]": options.resourceUids.join(","),
      "block_name[in]": options.blockNames.join(","),
    };

    if (options.page !== undefined) {
      params.page = options.page;
    }
    if (options.pageSize !== undefined) {
      params.page_size = options.pageSize;
    }

    return this.client
      .get(`/networks/${networkUid}/substations/info-data`, { params })
      .then((response) => response.data.data);
  }
}

// Export common types for this class
export type {
  ValidMeterComponent,
  ValidMeterStage,
  ValidMeter,
  BaseMeterDataParams,
  GetSingleSubstationMeterDataParams,
  GetSubstationsMeterDataParams,
  MeterDataResponse,
  MeterData,
};
